import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { Palette, Heart, Code, Coffee, Star, Menu, X, Puzzle } from 'lucide-react';
import Lenis from 'lenis';
import 'lenis/dist/lenis.css';
import SEO from './SEO';

const Story = () => {
  const { isDark, setIsDark } = useTheme();
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const lenisRef = React.useRef(null);

  // Safari detection for optimized transitions
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  // Initialize Lenis smooth scroll (Optimized for Safari)
  React.useEffect(() => {

    const lenis = new Lenis({
      duration: isSafari ? 0.8 : 1.0, // Faster on Safari
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // easeOutExpo
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: !isSafari, // Disable smooth scroll on Safari
      mouseMultiplier: isSafari ? 0.5 : 1,
      smoothTouch: false,
      touchMultiplier: 2,
      infinite: false,
      lerp: isSafari ? 0.15 : 0.1, // Snappier on Safari
    });

    lenisRef.current = lenis;

    // Reset scroll position immediately when component mounts
    lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);

    let rafId;
    function raf(time) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }

    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
      className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100 transition-colors duration-500"
    >
      <SEO
        title="Our Story - Komorebi Color Palette Generator"
        description="Learn about the inspiration behind Komorebi, a free color palette generator inspired by Japanese aesthetics and Sanzo Wada's color theory. Discover how we're making professional color tools accessible to everyone."
        canonical="https://ko-mo-re-bi.com/story"
        keywords="komorebi story, color tool development, Japanese design, Sanzo Wada, color theory, design philosophy, open source design tools"
      />
      {/* Skip to main content link for keyboard navigation */}
      <a
        href="#story-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-lg focus:shadow-lg"
      >
        Skip to story content
      </a>

      {/* Header - Fade only, no movement */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="sticky top-4 z-50 px-4"
      >
        <div className="max-w-[1200px] mx-auto">
          <header className={`relative border border-gray-200/50 dark:border-gray-800/50 backdrop-blur-md bg-white/70 dark:bg-gray-950/70 ${isMobileMenuOpen ? 'rounded-3xl' : 'rounded-full'} ${isScrolled ? 'shadow-lg shadow-gray-200/50 dark:shadow-gray-900/50' : ''}`} role="banner" style={{ transition: 'box-shadow 500ms' }}>
            <div className="px-6 py-3 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity" aria-label="Komorebi homepage">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg blur-lg opacity-50" />
                    <img src="/komorebi-logo.svg" alt="Komorebi - Professional Color Palette Generator Logo" className="w-6 h-6 relative z-10" />
                  </div>
                  <span className="text-base font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">Komorebi</span>
                </Link>
                <nav className="hidden md:flex items-center gap-2" role="navigation" aria-label="Main navigation">
                  <Link to="/#features" className="px-4 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-all duration-300">Features</Link>
                  <Link to="/story" className="px-4 py-1.5 text-sm font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/50 rounded-full" aria-current="page">Story</Link>
                  <Link to="/changelog" className="px-4 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-all duration-300">Changelog</Link>
                  <a
                    href="https://www.figma.com/community/plugin/1562754844917396329"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-all duration-300"
                    aria-label="Get Komorebi Figma Plugin"
                  >
                    <Puzzle className="w-3.5 h-3.5" />
                    Figma Plugin
                  </a>
                  <a
                    href="https://www.producthunt.com/posts/komorebi-color-play?utm_source=other&utm_medium=social"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-all duration-300"
                    aria-label="Review Komorebi on Product Hunt"
                  >
                    <Star className="w-3.5 h-3.5" />
                    Review
                  </a>
                </nav>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsDark(!isDark)}
                  className="px-3 py-1.5 rounded-full hover:bg-gray-200/80 dark:hover:bg-gray-800/80 backdrop-blur-sm transition-all duration-300 text-lg"
                  aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                  aria-pressed={isDark}
                >
                  {isDark ? '☀️' : '🌙'}
                </button>
                <Link
                  to="/app"
                  className="hidden sm:inline-flex px-4 py-1.5 bg-gray-900/80 dark:bg-white/80 backdrop-blur-xl text-white dark:text-gray-900 rounded-full font-semibold hover:bg-gray-900 dark:hover:bg-white transition-all duration-500 hover:scale-105 border border-gray-800 dark:border-gray-200 text-sm"
                  aria-label="Launch Komorebi color palette generator app"
                >
                  Launch App
                </Link>
                {/* Mobile Menu Button */}
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="md:hidden px-3 py-1.5 rounded-full hover:bg-gray-200/80 dark:hover:bg-gray-800/80 transition-all duration-300"
                  aria-label="Toggle mobile menu"
                  aria-expanded={isMobileMenuOpen}
                >
                  {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Mobile Menu Dropdown */}
            {isMobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="md:hidden border-t border-gray-200 dark:border-gray-800 py-4 px-6 rounded-b-2xl"
              >
                <nav className="flex flex-col gap-2" role="navigation" aria-label="Mobile navigation">
                  <Link
                    to="/#features"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-300"
                  >
                    Features
                  </Link>
                  <Link
                    to="/story"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="px-4 py-2.5 text-sm font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/50 rounded-lg"
                  >
                    Story
                  </Link>
                  <Link
                    to="/changelog"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-300"
                  >
                    Changelog
                  </Link>
                  <a
                    href="https://www.figma.com/community/plugin/1562754844917396329"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-300"
                  >
                    <Puzzle className="w-4 h-4" />
                    Figma Plugin
                  </a>
                  <a
                    href="https://www.producthunt.com/posts/komorebi-color-play?utm_source=other&utm_medium=social"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-300"
                  >
                    <Star className="w-4 h-4" />
                    Review on Product Hunt
                  </a>
                  <Link
                    to="/app"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="mt-2 px-4 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg font-semibold text-center border border-gray-800 dark:border-gray-200 text-sm transition-all duration-300"
                  >
                    Launch App
                  </Link>
                </nav>
              </motion.div>
            )}
          </header>
        </div>
      </motion.div>

      {/* Story Content */}
      <article id="story-content" className="max-w-7xl mx-auto px-6 py-20" role="main">
        {/* Hero - Slide up with fade */}
        <motion.header
          initial={{ opacity: 0, ...(isSafari ? {} : { y: 30 }) }}
          animate={{ opacity: 1, ...(isSafari ? {} : { y: 0 }) }}
          exit={{ opacity: 0, ...(isSafari ? {} : { y: -30 }) }}
          transition={{ duration: isSafari ? 0.3 : 0.5, delay: isSafari ? 0 : 0.1, ease: "easeInOut" }}
          className="text-center mb-24"
        >
          <h1 id="story-heading" className="text-5xl md:text-7xl font-bold mb-6" style={{ lineHeight: '1.3' }}>
            <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">The Story Behind </span>
            <span className="inline-block animate-morph-gradient font-black">Komorebi</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Building the color tool I always wanted
          </p>
        </motion.header>

        {/* Content Sections */}
        <div className="space-y-32">
          {/* The Problem - Left-aligned with large decorative icon */}
          <motion.div
            initial={{ opacity: 0, ...(isSafari ? {} : { y: 30 }) }}
            animate={{ opacity: 1, ...(isSafari ? {} : { y: 0 }) }}
            exit={{ opacity: 0, ...(isSafari ? {} : { y: -30 }) }}
            transition={{ duration: isSafari ? 0.3 : 0.5, delay: isSafari ? 0 : 0.2, ease: "easeInOut" }}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            <div className="order-2 md:order-1">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-600 flex items-center justify-center text-white shadow-lg shadow-blue-500/30">
                  <Palette className="w-8 h-8" />
                </div>
                <h2 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">The Problem</h2>
              </div>
              <div className="space-y-4 text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                <p>
                  As a maker, I spent countless hours jumping between tools to find the right colors.
                  Need a RAL code? Open another tab. Want to check accessibility? Another tool. Export to Tailwind?
                  Copy-paste gymnastics. It was exhausting.
                </p>
                <p>
                  Existing color tools either focused on one thing really well (like accessibility) or tried to do everything
                  with a bloated interface that got in the way. I wanted something different: professional-grade features
                  in a tool that felt effortless.
                </p>
              </div>
            </div>
            <div className="hidden md:flex order-1 md:order-2 justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-cyan-400/20 rounded-3xl blur-3xl"></div>
                <div className="relative w-64 h-64 rounded-3xl bg-gradient-to-br from-blue-100 to-cyan-100 dark:from-blue-900/30 dark:to-cyan-900/30 flex items-center justify-center border border-blue-200 dark:border-blue-800">
                  <Palette className="w-32 h-32 text-blue-600 dark:text-blue-400 opacity-50" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* The Solution - Right-aligned, alternating layout */}
          <motion.div
            initial={{ opacity: 0, ...(isSafari ? {} : { y: 30 }) }}
            animate={{ opacity: 1, ...(isSafari ? {} : { y: 0 }) }}
            exit={{ opacity: 0, ...(isSafari ? {} : { y: -30 }) }}
            transition={{ duration: isSafari ? 0.3 : 0.5, delay: isSafari ? 0 : 0.3, ease: "easeInOut" }}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            <div className="hidden md:flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/20 to-teal-400/20 rounded-3xl blur-3xl"></div>
                <div className="relative w-64 h-64 rounded-3xl bg-gradient-to-br from-cyan-100 to-teal-100 dark:from-cyan-900/30 dark:to-teal-900/30 flex items-center justify-center border border-cyan-200 dark:border-cyan-800">
                  <Code className="w-32 h-32 text-cyan-600 dark:text-cyan-400 opacity-50" />
                </div>
              </div>
            </div>
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-600 to-teal-600 flex items-center justify-center text-white shadow-lg shadow-cyan-500/30">
                  <Code className="w-8 h-8" />
                </div>
                <h2 className="text-4xl font-bold bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent">The Solution</h2>
              </div>
              <div className="space-y-4 text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                <p>
                  Komorebi (木漏れ日) is a Japanese word that describes sunlight filtering through trees. It's beautiful,
                  natural, and ephemeral, just like the perfect color palette. Komorebi was built to be your single source
                  of truth for color.
                </p>
                <p>
                  Every feature exists for a reason. 14+ color standards? Because makers work across digital and physical.
                  WCAG compliance? Because accessibility isn't optional. Advanced color spaces? Because LAB and OKLCH give
                  you perceptually uniform colors that actually look good together.
                </p>
                <p>
                  Every detail was carefully considered: one-click exports to every format you need, real-time color blindness
                  simulation, instant palette generation using proven color theory, and a clean interface that gets out
                  of your way.
                </p>
              </div>
            </div>
          </motion.div>

          {/* The Philosophy - Full-width featured card */}
          <motion.div
            initial={{ opacity: 0, ...(isSafari ? {} : { y: 30 }) }}
            animate={{ opacity: 1, ...(isSafari ? {} : { y: 0 }) }}
            exit={{ opacity: 0, ...(isSafari ? {} : { y: -30 }) }}
            transition={{ duration: isSafari ? 0.3 : 0.5, delay: isSafari ? 0 : 0.4, ease: "easeInOut" }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-teal-500/10 via-blue-500/10 to-purple-500/10 rounded-3xl blur-3xl"></div>
            <div className="relative rounded-3xl bg-white/50 dark:bg-gray-900/50 backdrop-blur-xl border border-gray-200 dark:border-gray-800 p-12 md:p-16">
              <div className="max-w-4xl mx-auto text-center">
                <div className="flex justify-center mb-8">
                  <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-teal-600 to-blue-600 flex items-center justify-center text-white shadow-2xl shadow-teal-500/30">
                    <Heart className="w-10 h-10" />
                  </div>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold mb-8 bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">The Philosophy</h2>
                <div className="space-y-6 text-xl text-gray-700 dark:text-gray-300 leading-relaxed">
                  <p className="text-2xl font-medium text-gray-900 dark:text-gray-100">
                    Great tools should be invisible.
                  </p>
                  <p>
                    They shouldn't require tutorials or onboarding flows.
                    They should just work, the way you expect them to. That's why Komorebi is free, requires no signup,
                    and works entirely in your browser.
                  </p>
                  <p>
                    This isn't about building a "SaaS empire". Komorebi is a tool that solves a real problem. If it helps you
                    ship better products, that's success.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* The Maker - Featured card with avatar placeholder */}
          <motion.div
            initial={{ opacity: 0, ...(isSafari ? {} : { y: 30 }) }}
            animate={{ opacity: 1, ...(isSafari ? {} : { y: 0 }) }}
            exit={{ opacity: 0, ...(isSafari ? {} : { y: -30 }) }}
            transition={{ duration: isSafari ? 0.3 : 0.5, delay: isSafari ? 0 : 0.5, ease: "easeInOut" }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-3xl blur-3xl"></div>
            <div className="relative rounded-3xl bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30 border border-purple-200 dark:border-purple-800 p-12 md:p-16">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-6 mb-8">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white shadow-2xl shadow-purple-500/30 flex-shrink-0">
                    <Coffee className="w-10 h-10" />
                  </div>
                  <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">The Maker</h2>
                </div>
                <div className="space-y-6 text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                  <p className="text-xl">
                    Komorebi was built by{" "}
                    <a
                      href="https://obelisk.agency"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors underline decoration-2 underline-offset-2"
                    >
                      Obelisk
                    </a>
                    , a design and development studio tired of juggling multiple tools just to pick the right colors.
                    After years of switching between color pickers, accessibility checkers, and format converters,
                    we decided to build the tool we always wished existed.
                  </p>
                  <p>
                    What started as a weekend project evolved into a comprehensive color tool that combines professional-grade
                    features with an interface that doesn't get in your way. We obsessed over every detail, from the smooth
                    color transitions to the one-click exports, to create a tool that feels as good to use as it is powerful.
                  </p>
                  <div className="pt-4 flex items-center gap-3 text-base text-gray-600 dark:text-gray-400">
                    <Heart className="w-5 h-5 text-red-500 fill-current" />
                    <span>Built with love and coffee in Iasi, Romania 🇷🇴</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* CTA - Large, prominent call to action */}
          <motion.div
            initial={{ opacity: 0, ...(isSafari ? {} : { scale: 0.95 }) }}
            animate={{ opacity: 1, ...(isSafari ? {} : { scale: 1 }) }}
            exit={{ opacity: 0, ...(isSafari ? {} : { scale: 0.95 }) }}
            transition={{ duration: isSafari ? 0.3 : 0.5, delay: isSafari ? 0 : 0.6, ease: "easeInOut" }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-teal-500 rounded-3xl blur-2xl opacity-20"></div>
            <div className="relative rounded-3xl bg-gradient-to-br from-gray-900 to-gray-800 dark:from-white dark:to-gray-50 p-16 text-center border border-gray-800 dark:border-gray-200 overflow-hidden">
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-500/10 to-transparent rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-teal-500/10 to-transparent rounded-full blur-3xl"></div>

              <div className="relative z-10">
                <h3 className="text-4xl md:text-5xl font-bold mb-6 text-white dark:text-gray-900">Ready to try it?</h3>
                <p className="text-xl mb-10 text-gray-300 dark:text-gray-600 max-w-2xl mx-auto">
                  Experience the color tool that makers love
                </p>
                <Link
                  to="/app"
                  className="inline-block px-12 py-4 bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl text-gray-900 dark:text-white rounded-full font-bold text-lg hover:bg-white dark:hover:bg-gray-900 transition-all duration-500 hover:scale-105 border border-gray-200 dark:border-gray-800 shadow-2xl hover:shadow-3xl"
                  aria-label="Launch color palette generator application"
                >
                  Launch App →
                </Link>
                <p className="text-sm mt-6 text-gray-400 dark:text-gray-600">
                  Share your designs & palettes with <span className="font-semibold text-gray-300 dark:text-gray-500">#komorebicolorplay</span>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </article>

      {/* Footer - Slide up from bottom */}
      <motion.footer
        initial={{ opacity: 0, ...(isSafari ? {} : { y: 20 }) }}
        animate={{ opacity: 1, ...(isSafari ? {} : { y: 0 }) }}
        exit={{ opacity: 0, ...(isSafari ? {} : { y: 20 }) }}
        transition={{ duration: isSafari ? 0.3 : 0.5, delay: isSafari ? 0 : 0.7, ease: "easeInOut" }}
        className="relative border-t border-gray-200/50 dark:border-gray-800/50 backdrop-blur-sm bg-white/30 dark:bg-gray-950/30 py-12"
        role="contentinfo"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <img src="/komorebi-logo.svg" alt="Komorebi" className="w-6 h-6" />
              <span className="font-semibold">Komorebi</span>
            </Link>
            <div className="flex gap-6 text-sm text-gray-600 dark:text-gray-400">
              <Link to="/#features" className="hover:text-blue-600 transition-colors duration-300">Features</Link>
              <Link to="/story" className="hover:text-blue-600 transition-colors duration-300">Story</Link>
              <Link to="/changelog" className="hover:text-blue-600 transition-colors duration-300">Changelog</Link>
              <Link to="/app" className="hover:text-blue-600 transition-colors duration-300">App</Link>
              <a href="mailto:hello@obelisk.agency" className="hover:text-blue-600 transition-colors duration-300">Contact</a>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <span>made with</span>
              <Heart className="h-4 w-4 text-red-500 fill-current" />
              <span>and</span>
              <Coffee className="h-4 w-4 text-amber-600" />
              <span>by</span>
              <a
                href="https://obelisk.agency"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors duration-200"
              >
                obelisk
              </a>
            </div>
          </div>
        </div>
      </motion.footer>
    </motion.div>
  );
};

export default Story;
